import { FC, memo, useCallback, useEffect } from 'react';
import { Card, Col, Flex, Form, Row, Typography } from 'antd';
import { formatNumberWithSpaces } from '@medicusunion/front-utils';
import {
  FundViewOutlined,
  StockOutlined,
  TagOutlined,
  UserSwitchOutlined
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  selectAnalyticsAnalytics,
  selectAnalyticsRevenuePrice,
  selectAnalyticsRevenueQuantity
} from 'store/analytics/selectors';
import { AnalyticsData } from 'store/analytics/types';
import { shallowEqual } from 'react-redux';
import dayjs from 'dayjs';
import { fetchAnalyticsData } from 'store/analytics/slice';
import { useOrganizationsStateContext } from 'providers/Organizations';

import { CardIcon } from './styled';
import { money } from 'utils/price';
import XYChart from '../XYChart';
import Filters from '../Filters';
import { AnalyticsFiltersType } from '../../_types';
import { analyticsReviewCache } from 'utils/cacheWithQuery';

const ANALYTICS_DATA = [
  {
    title: 'Total Users',
    colors: ['rgba(53, 182, 120, 0.1)', '#35B678'],
    key: 'totalUsers',
    type: 'number',
    icon: UserSwitchOutlined
  },
  {
    title: 'Total Orders',
    colors: ['rgba(255, 127, 78, 0.1)', '#FF7F4E'],
    key: 'totalOrders',
    type: 'number',
    icon: TagOutlined
  },
  {
    title: 'Conversion Rate',
    colors: ['rgba(255, 76, 78, 0.1)', '#FF4C4E'],
    key: 'conversionRate',
    type: 'percent',
    icon: StockOutlined
  },
  {
    title: 'Revenue Share',
    colors: ['#EFF3FF', '#2552F3'],
    key: 'revenue',
    type: 'money',
    icon: FundViewOutlined
  }
] as const;

interface IProps {
  utmSource?: string;
}

const DashboardReview: FC<IProps> = ({ utmSource }) => {
  const [form] = Form.useForm();

  const { parentSelected } = useOrganizationsStateContext();

  const dispatch = useAppDispatch();
  const analytics = useAppSelector(
    selectAnalyticsAnalytics(parentSelected),
    shallowEqual
  );

  const quantity = useAppSelector(selectAnalyticsRevenueQuantity, shallowEqual);
  const prices = useAppSelector(
    selectAnalyticsRevenuePrice(parentSelected),
    shallowEqual
  );

  useEffect(() => {
    analyticsReviewCache.init();
  }, []);

  const fetchData = useCallback(
    (values: AnalyticsFiltersType, utm_source?: string) => {
      const { dates, ...rest } = values;

      if (!dates) {
        return;
      }

      const [start, end] = dates;

      dispatch(
        fetchAnalyticsData({
          startDate: dayjs(start).format('YYYY-MM-DD'),
          endDate: dayjs(end).format('YYYY-MM-DD'),
          utm_source,
          ...rest
        })
      );
    },
    [dispatch]
  );

  useEffect(() => {
    const values = form.getFieldsValue([
      'dates',
      'utm_medium',
      'utm_campaign',
      'utm_term'
    ]);

    fetchData(values, utmSource);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utmSource]);

  const onFilter = useCallback(
    (values: AnalyticsFiltersType) => {
      fetchData(values, utmSource);
    },
    [fetchData, utmSource]
  );

  const renderAnalyticsValue = (key: keyof AnalyticsData, type: string) => {
    const value = parseFloat(analytics[key]);

    if (!value) {
      return '0';
    }

    switch (type) {
      case 'number':
        return formatNumberWithSpaces(value);
      case 'percent':
        return `${formatNumberWithSpaces(+(value * 100).toFixed(2))}%`;
      case 'money':
        return money(value, { currency: 'USD' });
      default:
        return value;
    }
  };

  return (
    <>
      <Form
        form={form}
        initialValues={{
          dates: [dayjs().add(-7, 'd').startOf('day'), dayjs().endOf('day')]
        }}
        onFinish={onFilter}
      >
        <Filters dateMode="date" onReset={() => form.submit()} />
      </Form>
      <Flex gap={60} vertical>
        <div>
          <Typography.Title level={4}>Analytics</Typography.Title>
          <Row gutter={24}>
            {ANALYTICS_DATA.map(item => (
              <Col key={item.key} span={24 / ANALYTICS_DATA.length}>
                <Card>
                  <Flex justify="space-between">
                    <div>
                      <Typography.Text>{item.title}</Typography.Text>
                      <Typography.Title level={3}>
                        {renderAnalyticsValue(item.key, item.type)}
                      </Typography.Title>
                    </div>
                    <CardIcon $color={item.colors[0]}>
                      <item.icon
                        style={{ fontSize: 24, color: item.colors[1] }}
                      />
                    </CardIcon>
                  </Flex>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
        <Row gutter={24}>
          <Col span={12}>
            <Typography.Title level={4}>Revenue Share</Typography.Title>
            <XYChart
              data={[
                {
                  label: 'Subscription Year',
                  value: prices.subscription_yearly,
                  color: '#35B678'
                },
                {
                  label: 'Subscription Mo',
                  value: prices.subscription_monthly,
                  color: '#6ED5A3'
                },
                {
                  label: 'Radiology',
                  value: prices.radiology,
                  color: '#64B5F6'
                },
                {
                  label: 'Video Consultation',
                  value: prices.video_consultation,
                  color: '#8FCDFF'
                }
              ]}
              formatValue={value => money(value, { currency: 'USD' })}
            />
          </Col>
          <Col span={12}>
            <Typography.Title level={4}>Funnel Table</Typography.Title>
            <XYChart
              data={[
                {
                  label: 'Subscription Year',
                  value: quantity.subscription_yearly,
                  color: '#35B678'
                },
                {
                  label: 'Subscription Mo',
                  value: quantity.subscription_monthly,
                  color: '#6ED5A3'
                },
                {
                  label: 'Radiology',
                  value: quantity.radiology,
                  color: '#64B5F6'
                },
                {
                  label: 'Video Consultation',
                  value: quantity.video_consultation,
                  color: '#8FCDFF'
                }
              ]}
              itemHeight={40}
            />
          </Col>
        </Row>
      </Flex>
    </>
  );
};

export default memo(DashboardReview);
